
import React, { useState, useEffect } from 'react';
import '../styles/bootstrap.min.css';
import '../styles/style.css';
import logo from '../assets/images/logo.svg';
import passwordeye from '../assets/images/eye.svg';
import bg_blue_wrap from '../assets/images/bg_blue.svg';
import xlogo from '../assets/images/x_logo.png';
import glogo from '../assets/images/social_g.png';
import fblogo from '../assets/images/facebook.png';
import applelogo from '../assets/images/apple-black-logo.png';
import FeaturedPost from '../components/FeaturedPost';
import Footer from '../components/Footer';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from 'react-google-login';
import config from '../config/config';

const ModalOverlay = ({ show, onClick }) => {
  return show ? <div className="modal-backdrop fade show" onClick={onClick}></div> : null;
};

const Login = () => {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalEmailError, setModalEmailError] = useState('');
  const [modalLoading, setModalLoading] = useState(false); // New state for modal button
  const [wordCount, setWordCount] = useState(0);
  const [totpCode, setTotpCode] = useState('');
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Track loading state

  useEffect(() => {
    // Extract token and user_role from the query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userRole = urlParams.get('user_role');

    if (token && userRole) {
      localStorage.setItem('token', token);
      localStorage.setItem('user_role', userRole);
      navigate('/Home');
    }
  }, [navigate]);

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setModalEmail('');
    setModalEmailError('');
    setWordCount(0);
    setModalLoading(false); // Reset modal loading state
  };

  const handleLogin = async (e) => {
    e.preventDefault();
  
    setEmailError('');
    setPasswordError('');
    setMessage({ text: '', type: '' });
  
    if (!email) {
      setEmailError('Email is required');
      return;
    }
  
    if (!password) {
      setPasswordError('Password is required');
      return;
    }
  
    setLoading(true); // Start loading

    try {
      const response = await fetch(`${config.baseURL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        if (data.user_id) {
          setUserId(data.user_id);
          setShowTotpModal(true);
        } else {
          if (data.token && data.user_role) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('user_role', data.user_role);
            setMessage({ text: 'Login successful', type: 'success' });
            navigate('/Home');
          } else {
            setMessage({ text: 'Login failed. Please check your credentials.', type: 'error' });
          }
        }
      } else {
        setMessage({ text: data.message || 'Login failed. Please check your credentials.', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
    } finally {
      setLoading(false); // Stop loading after request
    }
  };

  const handleVerifyTotp = async () => {
    if (!totpCode || !userId) {
      setMessage({ text: 'Please enter a valid verification code.', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${config.baseURL}/verify-topt-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          totp_code: totpCode
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.token && data.user_role) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user_role', data.user_role);
        }
        setMessage({ text: 'Login successful', type: 'success' });
        setShowTotpModal(false);
        navigate('/Home');
      } else {
        setMessage({ text: data.message || 'Invalid verification code', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRestoreAccess = async (e) => {
    e.preventDefault();
    setModalLoading(true); // Disable button
    setModalEmailError('');

    if (!modalEmail) {
      setModalEmailError('The Email field is required');
      setModalLoading(false); // Re-enable button
      return;
    } else if (!/\S+@\S+\.\S+/.test(modalEmail)) {
      setModalEmailError('Email is not valid.');
      setModalLoading(false); // Re-enable button
      return;
    }

    try {
      const response = await fetch(`${config.baseURL}/forget-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: modalEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ text: 'Password reset link sent to your email.', type: 'success' });
        handleClose();
      } else {
        setModalEmailError(data.message || 'Failed to send reset link. Please try again later.');
      }
    } catch (error) {
      setModalEmailError('An error occurred. Please try again later.');
      console.error('Forgot password error:', error);
    } finally {
      setModalLoading(false); // Re-enable button
    }
  };

  useEffect(() => {
    setWordCount(modalEmail.length);
  }, [modalEmail]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const oauthToken = queryParams.get('oauth_token');
    const oauthVerifier = queryParams.get('oauth_verifier');
    const roleType = 'user';

    const handleAuthResponse = async () => {
      try {
        const response = await fetch(`${config.baseURL}/auth/twitter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            oauth_token: oauthToken,
            oauth_verifier: oauthVerifier,
            role_type: roleType,
          }),
        });

        const result = await response.json();
        if (result.success) {
          console.log('Twitter login successful:', result);
          localStorage.setItem('oauth_token', result.token);
          navigate('/Home');
        } else {
          console.error('Twitter login failed:', result.message);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    if (oauthToken && oauthVerifier) {
      handleAuthResponse();
    }
  }, [navigate]);

  const handleTwitterLogin = () => {
    const twitterAuthUrl = `${config.baseURL}/auth/twitter`;
    window.open(twitterAuthUrl, '_blank', 'noopener,noreferrer');
  };

  const handleFacebookLogin = () => {
    const clientId = '503205819153203';
    const redirectUri = `${config.baseURL}/auth/facebook/callback`;
    const authorizationUrl = `https://www.facebook.com/v20.0/dialog/oauth?` +
      new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: 'email',
        response_type: 'code',
        state: 'some_random_string',
      }).toString();

    window.location.href = authorizationUrl;
  };

  const handleFacebookOAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      try {
        const response = await fetch(`${config.baseURL}/auth/facebook/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            role_type: 'user',
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Facebook login failed');
        }

        if (result.success === false && result.error === true) {
          console.error(result.message);
        } else {
          console.log('Facebook login successful:', result);
          const loginUrl = `https://patreonpass.bvmwebsolutions.com/login?token=${encodeURIComponent(result.token)}&user_role=${encodeURIComponent(result.user_role)}`;
          window.location.href = loginUrl;
        }
      } catch (error) {
        console.error('Facebook login failed:', error);
      }
    }
  };

  useEffect(() => {
    handleFacebookOAuthCallback();
  }, []);

  const handleGoogleLogin = () => {
    const clientId = '1087363659375-a94omcoskevklauhjeh1of9pbfcpi14r.apps.googleusercontent.com';
    const redirectUrl = `${config.baseURL}/auth/google/callback`;
    const scopes = ['profile', 'email'];
    const authorizationUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUrl,
        scope: scopes.join(' '),
        response_type: 'code',
        access_type: 'offline',
      }).toString();

    window.location.href = authorizationUrl;
  };

  const handleOAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      try {
        const response = await fetch(`${config.baseURL}/auth/google/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            role_type: 'user',
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Google login failed');
        }

        if (result.success === false && result.error === true) {
          console.error(result.message);
        } else {
          console.log('Google login successful:', result);
          const loginUrl = `https://patreonpass.bvmwebsolutions.com/login?token=${encodeURIComponent(result.token)}&user_role=${encodeURIComponent(result.user_role)}`;
          window.location.href = loginUrl;
        }
      } catch (error) {
        console.error('Google login failed:', error);
      }
    }
  };

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleAppleLogin = () => {
    const clientId = 'com.yourapp.clientid';
    const redirectUri = `${config.baseURL}/auth/apple/callback`;
    const scopes = ['name', 'email'];
    const authorizationUrl = `https://appleid.apple.com/auth/authorize?` +
      new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: scopes.join(' '),
        response_type: 'code',
        response_mode: 'form_post',
      }).toString();
    window.location.href = authorizationUrl;
  };

  return (
    <div>
      <div className="login_wrap">
        <div className="login_wrap_container">
          <div className="right_login_box">
            <div className="img_content">
              <img src={logo} alt="logo" />
              <h4>Sign up to support your favorite creators</h4>
            </div>
          </div>
          <div className="left_login_box">
            <div className="login_box">
              <div id="form-container">
                <form id="loginForm" onSubmit={handleLogin}>
                  <p className="p_sub_head">Log in</p>
                  <div className="mb-4 position_relative">
                    <input 
                      type="email" 
                      className={`form-control ${emailError ? 'is-invalid' : ''}`} 
                      id="emailAddress"                        
                      placeholder="Email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <label htmlFor="emailAddress" className="placeholder-label">Email</label>
                    {emailError && <div className="invalid-feedback">{emailError}</div>}
                  </div>
                  <div className="mb-4 pwd_side position_relative">
                    <input 
                      type={showPassword ? 'text' : 'password'} 
                      className={`form-control ${passwordError ? 'is-invalid' : ''}`} 
                      id="loginPassword"                       
                      placeholder="Password" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <label htmlFor="loginPassword" className="placeholder-label">Password</label>
                    <img 
                      className="password-toggle" 
                      src={passwordeye} 
                      alt="Show Password" 
                      onClick={toggleShowPassword}
                      style={{ cursor: 'pointer' }}
                    />
                    {passwordError && <div className="invalid-feedback">{passwordError}</div>}
                  </div>

                  {message.text && (
                    <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
                      {message.text}
                    </div>
                  )}

                  <div className="d-grid my-4 mb-0 mb-4">
                    <button 
                      className="btn btn-primary" 
                      type="submit" 
                      disabled={loading}
                      aria-disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Logging in...
                        </>
                      ) : 'Log In'}
                    </button>
                  </div>

                  <div className="row">
                    <div className="col text-center space_texts">
                      <a onClick={handleShow} href="javascript:void(0)">Forgot Password?</a>
                      <Link to="/register" className="signup">Sign up for PatreonPass</Link>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <div className="social_logins"> 
              <Link to="/creator-register" className="button button--social-login button--create">Signup as a creator</Link>
              <Link className="button button--social-login button--x" onClick={handleTwitterLogin}><img src={xlogo} alt="Sign in with X" /> Sign in with X</Link>
              <Link className="button button--social-login button--googleplus" onClick={handleGoogleLogin}><img src={glogo} alt="Sign in with Google" /> Sign in with Google</Link>
              <Link className="button button--social-login button--facebook" onClick={handleFacebookLogin}><img src={fblogo} alt="Sign in with Facebook" /> Sign in with Facebook</Link>
              <Link className="button button--social-login button--apple" onClick={handleAppleLogin}><img src={applelogo} alt="Sign in with Apple" /> Sign in with Apple</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Restore Access Modal */}
      {showModal && (
        <>
          <ModalOverlay show={showModal} onClick={handleClose} />
          <div className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="restoreAccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="restoreAccessModalLabel">Restore Access</h5>
                </div>
                <div className="modal-body">
                  <form id="restoreAccessForm" onSubmit={handleRestoreAccess}>
                    <p>If you have a PatreonPass account, you will receive a password reset link to this e-mail.</p>
                    <div className="position_relative">
                      <input 
                        type="email" 
                        className={`form-control ${modalEmailError ? 'is-invalid' : ''}`} 
                        id="restoreAccessEmail" 
                        placeholder="Email" 
                        value={modalEmail}
                        onChange={(e) => setModalEmail(e.target.value)}
                      />
                      <label htmlFor="restoreAccessEmail" className="placeholder-label">Email</label>
                      {modalEmailError && <div className="invalid-feedback">{modalEmailError}</div>}
                    </div>                   
                    <div className="word_count">
                      <span>{wordCount} / 64</span>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary send_btn" onClick={handleClose}>Cancel</button>
                      <button 
                        type="submit" 
                        disabled={modalLoading}
                        aria-disabled={modalLoading}
                        className="btn btn-primary send_btn remove_right_side_margin"
                      >
                        {modalLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : 'Send'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* TOTP Verification Modal */}
      {showTotpModal && (
        <>
          <ModalOverlay show={showTotpModal} onClick={() => setShowTotpModal(false)} />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Two-Factor Authentication</h5>
                </div>
                <div className="modal-body">
                  <p>Please enter the verification code from your authenticator app.</p>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter verification code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      maxLength="6"
                    />
                  </div>
                  {message.text && (
                    <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mt-3`}>
                      {message.text}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowTotpModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleVerifyTotp}>Verify</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <FeaturedPost />
      <Footer />
    </div>
  );
};

export default Login;
